/**
 * Project Approval — the CEO screen (Jay's email 2026-08-11, item 3).
 *
 * Rule being implemented: no project is live until it is approved here, and no ticket can be
 * issued against a project that is not approved. The queue is deliberately plain — the CEO
 * should be able to clear it in a couple of minutes: one row per project, Approve / Reject
 * inline, a reason box on Reject.
 *
 * The Settings tab (admins only) holds the approver mailbox, so switching the test recipient (max@)
 * to jay@ and later to y@helinox.com never needs a deploy. Team membership is NOT here — a Sales OP
 * member is a portal account, so it is set in Account → List Users → Edit.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { CheckCircle2, RotateCcw, Pencil, Eye, Mail, Loader2, Trash2, ChevronRight, X } from 'lucide-react';
import Header from './Header';
import TabIcon from './TabIcon';
import ApprovalCharts from './ApprovalCharts';
import ModuleHelp from './ModuleHelp';
import useAuth from './useAuth';
import { padProjectId } from './projectId';
import ProjectPhotos from './ProjectPhotos';
import './ProjectPhotos.css';
import { BACKEND_URL } from './backendConfig';
import DecisionDialog, { DECISION_MAX } from './DecisionDialog';
import useDiscardGuard from './useDiscardGuard';
import { fmtDateTimeVN } from './timeZone';
// The edit/view form is the tracker's New Project form, reused rather than re-implemented so the
// two cannot drift apart. That also pulls in the tracker's modal styling.
import {
  SkuCard, emptySku, monthToDisplay, ProjectInfoFields, skuWas, skuCardMissing,
  SortTh, sortRows, rowPassesColumnFilters, distinctColumnValues, stageColumns, firstNameOf,
  seasonOptions, seasonPartsOrdered,
} from './15_SampleTracker';
import './SampleTracker.css';
import './ProjectApproval.css';
import { DateChangeList } from './ProjectDates';
import { launchCheck, launchErrors, launchFlags } from './projectLaunchCheck';

/* Re-exported because this screen is where the dialog used to live: a reader who goes looking for it
   here should be sent on rather than find nothing. */
export { DecisionDialog, DECISION_MAX };

const API_BASE = `${BACKEND_URL}/api/development/project-tracker`;

// Identity-comparable default, so "have the option lists been fetched yet?" is a cheap check.
const EMPTY_FILTER_OPTIONS = {
  assignees: [], skuDevelopers: [], skuDesigners: [],
  allocations: [], projectCategories: [], projectStatuses: [],
  projectTypologies: [], projectStageStatuses: [], projectProductTypes: [],
};

// One place that decides what a column is called and how its value is read, so sorting, the
// Excel-style filter menu and the cell itself can never disagree.
/* People columns print the given name only — "Tina Lee" reads as "Tina" (Jay, 2026-08-25). It is done
   in `get`, not in the cell, so the sort order and the filter menu show the same text the row does.
   Widths were cut across the board in the same pass: the table needed a horizontal scrollbar to reach
   the Action column, and the people columns were the emptiest of them ("Big gaps"). */
const APPROVAL_COLUMNS = [
  { key: 'groupId', label: 'pa.colProjectId', get: (g) => g.groupId, width: 96 },
  { key: 'groupName', label: 'pa.colProjectName', get: (g) => g.groupName, width: 240 },
  // 140, not 112: "Sales OP Team 1" wrapped onto a second line and doubled the height of every row.
  { key: 'teamName', label: 'pa.colTeam', get: (g) => g.teamName, width: 140 },
  // Stamped onto the row from the team config — the approvals API knows the team, not who runs it.
  { key: 'managerNames', label: 'pa.colManager', get: (g) => firstNameOf(g.managerNames), width: 96 },
  { key: 'ownerName', label: 'pa.colOwner', get: (g) => firstNameOf(g.ownerName), width: 92 },
  // Young asked for this next to Project Owner — it is the field he checks before approving.
  { key: 'salesRepName', label: 'pa.colSalesRep', get: (g) => firstNameOf(g.salesRepName), width: 92 },
  { key: 'category', label: 'pa.colCategory', get: (g) => g.category, width: 132 },
  { key: 'season', label: 'pa.colSeason', get: (g) => g.season, width: 80 },
  { key: 'partnerName', label: 'pa.colPartner', get: (g) => g.partnerName, width: 118 },
  { key: 'targetLaunchMonth', label: 'pa.colTargetLaunch', get: (g) => g.targetLaunchMonth, width: 100 },
  { key: 'skuCount', label: 'pa.colSkus', get: (g) => g.skuCount, num: true, width: 64 },
  { key: 'createdBy', label: 'pa.colRequestedBy', get: (g) => g.createdBy, width: 96 },
];
const NOT_YOUR_TEAM_KEY = 'pa.notYourTeam';
const CHECK_W = 36;
const NO_W = 44;
// The column is sized to the buttons a given tab actually renders — Approved only ever shows View,
// so reserving room for three buttons there wasted 90px of a table that already scrolls sideways.
// The column is sized from the buttons a row ACTUALLY renders. Sizing it by role was wrong: a Sale
// manager has Edit but not Approve, so the old branch fell through to "View alone" (104px) and Edit
// wrapped onto a second line, doubling every row's height (Max, 2026-08-19).
/* Measured off the rendered buttons in the browser, not estimated: the guessed numbers left 71px of
   empty navy at the end of the Action column for an admin, who sees all four (Max, 2026-08-22:
   "cột action ở Admin và manager bị dư 1 khoảng phía cuối"). `submit` covers both things that slot
   holds — the Submit button and the shorter "Sent" label that replaces it. */
/* `drop` is the Delete button. It was missing here while the button was already rendering, so
   actionWidthFor returned NaN for an admin row and the Approve button hung outside the frozen
   column (Max, 2026-09-02, screenshot). */
/* `submit` went 88 -> 90 on 2026-10-02 when the "Sent" label became a Remind BUTTON. The note above
   says this slot holds Submit and whatever replaces it, so it is sized by the WIDEST of them;
   Remind measures 90 in the browser against Submit's 86. Measured, not guessed, like the rest. */
const ACTION_BTN_W = { view: 72, edit: 68, approve: 95, resubmit: 104, submit: 90, drop: 82 };
const ACTION_GAP = 8;
const ACTION_CELL_PAD = 20;                  // the td's own left+right padding
const actionWidthFor = (names) => names.reduce((sum, n) => sum + ACTION_BTN_W[n], 0)
    + (names.length - 1) * ACTION_GAP + ACTION_CELL_PAD;
// Columns that only make sense on one tab. A decision is only meaningful with WHO and WHEN next to
// it, so every tab carries its own timestamps. `approved_by` / `approved_at` hold the decision maker
// and time for a rejection too, which is why they are relabelled per tab.
const requestedCol = { key: 'requestedAt', label: 'pa.colRequestedAt', get: (g) => fmtDateTimeVN(g.approvalRequestedAt || g.createdAt, ''), width: 130 };
// What the manager wrote when they resubmitted — the approver needs it to see what changed.
const managerNoteCol = { key: 'approvalNote', label: 'pa.colManagerNote', get: (g) => g.approvalNote, width: 200 };
const APPROVAL_EXTRA = {
  pending: [requestedCol, managerNoteCol],
  approved: [
    requestedCol,
    { key: 'approvedBy', label: 'pa.colApprovedBy', get: (g) => firstNameOf(g.approvedBy), width: 100 },
    { key: 'approvedAt', label: 'pa.colApprovedAt', get: (g) => fmtDateTimeVN(g.approvedAt, ''), width: 146 },
    // What the CEO wrote when he approved. It was already stored and already shown inside the
    // project, but not in the list — so nobody knew a project carried a remark (Max, 2026-08-26).
    { key: 'decisionNote', label: 'pa.colApprovedOpinion', get: (g) => g.decisionNote, width: 240 },
  ],
  rejected: [
    requestedCol,
    { key: 'approvedBy', label: 'pa.colRejectedBy', get: (g) => firstNameOf(g.approvedBy), width: 100 },
    { key: 'approvedAt', label: 'pa.colRejectedAt', get: (g) => fmtDateTimeVN(g.approvedAt, ''), width: 146 },
    { key: 'rejectionReason', label: 'pa.colReason', get: (g) => g.rejectionReason, width: 240 },
  ],
};

/* THE REJECTED TAB IS BACK — Jay, 2026-10-16: "Also please show Rejected ones in this screen", "It shows
   the rejected but not re-submitted ones", "REJECTED ones should never be forgotten. Need attention and
   quick action." It went on 2026-09-21 because a refused item had moved into Pending and a second list
   of the same projects read as the screen saying one thing twice. Jay's point is the other half: in
   Pending a refused project was one red row among the waiting ones, and nobody can tell that red means
   rejected. So refused items STAY in Pending (they are still something to do) and ALSO have their own
   list, which is what the server has sent all along (`rejected`: projects holding a refused item not
   yet resubmitted). Between Pending and Approved, because it is the one that needs somebody to act. */
/* THE GUIDE (Max 01/11: every module has one, ending on the manual — this screen is in the Project Tracker handbook). */
const TOUR = [
  { selector: '.pa-tabrow .view-tabs', title: 'tourPa.tabsT', body: 'tourPa.tabsB' },
  { selector: '.pa-tabrow .pac-group', title: 'tourPa.chartsT', body: 'tourPa.chartsB' },
  { selector: '.pa-toolbar', title: 'tourPa.toolbarT', body: 'tourPa.toolbarB' },
  { selector: '.pa-section table', title: 'tourPa.listT', body: 'tourPa.listB' },
];

const TABS = [
  { key: 'pending', label: 'pa.tabPending' },
  { key: 'rejected', label: 'pa.tabRejected' },
  { key: 'approved', label: 'pa.tabApproved' },
];

/* SEARCH — Jay, same day: "to search/find easily … there's no search box as well". One box over the
   project, its SKUs and the people on it. An id is matched with or without its leading zeros, because
   every mail written before 2026-09-15 says PRJ-176 where the row says PRJ-00176. */
const searchNorm = (v) => String(v || '').toLowerCase().replace(/-0+(\d)/g, '-$1');
const rowMatches = (r, q) => [r.groupId, r.groupName, r.teamName, r.teamKey, r.ownerName, r.managerNames,
  r.salesRepName, r.partnerName, r.category, r.season, r.createdBy,
  ...(Array.isArray(r.skuRows) ? r.skuRows : []).flatMap((x) => [x.projectId, x.itemName])]
  .some((v) => searchNorm(v).includes(q));

const api = async (path, options = {}) => {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || i18n.t('xPa.requestFailed', { status: res.status }));
  return data;
};

const ProjectApproval = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  /* The tab can arrive in the URL — the CEO and Sales OP home cards link straight to the list they
     counted, and landing on Pending when the card said Rejected is landing on the wrong list
     (Max, 2026-09-15). Read once, at mount: this is where somebody ARRIVES, not a filter that keeps
     being reapplied while they click around. */
  const [tab, setTab] = useState(() => {
    const want = new URLSearchParams(window.location.search).get('tab');
    return ['pending', 'rejected', 'approved'].includes(want) ? want : 'pending';
  });
  /* ONE ROW clicked on the home page, not a whole card — Max, 2026-09-15: "dẫn đến đúng trang nhưng
     nó ko highlight phiếu lên á". Arriving on the right tab and leaving somebody to find PRJ-180
     among eight is arriving, not landing. Read once at mount and cleared as soon as it has been
     seen, so the highlight does not follow the person around while they work down the list. */
  const [focusId, setFocusId] = useState(
    /* Padded: a link written before 2026-09-15 says PRJ-180 and every row now says PRJ-00180. */
    () => padProjectId(new URLSearchParams(window.location.search).get('project') || ''),
  );
  const focusRow = useRef(null);
  const [queue, setQueue] = useState({ pending: [], approved: [], rejected: [] });
  const [charts, setCharts] = useState(null);
  const [approverName, setApproverName] = useState('');
  /* Scroll the row into view once the queue that contains it has arrived — hence `queue` in the
     deps, and hence this sitting BELOW it: declared above, the effect referenced a const in its own
     temporal dead zone and took the whole screen down with it. */
  useEffect(() => {
    if (!focusId || !focusRow.current) return undefined;
    focusRow.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // Long enough to be seen, short enough to stop being part of the table.
    const timer = setTimeout(() => setFocusId(''), 6000);
    return () => clearTimeout(timer);
  }, [focusId, queue, tab]);
  const [config, setConfig] = useState(null);
  const [me, setMe] = useState(null);   // who is looking: team, manager flag, admin flag
  const [busyId, setBusyId] = useState('');
  // One dialog for every decision (Max's manager, 2026-08-12): approve, reject and resubmit all
  // take a note, so they share a form instead of Approve being a bare click.
  //   { mode: 'single' | 'bulk', action: 'approve' | 'reject' | 'resubmit', group?, ids? }
  const [decisionFor, setDecisionFor] = useState(null);
  const [decisionNote, setDecisionNote] = useState('');
  /* Which long note is opened out, keyed by row + column — Jay, 2026-09-16: "The interface is too
     large when a comment is long. Could you standardize the height by displaying comments in a
     single row — collapsing them and only expanding upon click?"

     Per CELL, not per row: a rejected project can carry a reason and a manager's note side by side,
     and opening one to read it should not push the other one's row about as well. */
  const [openNotes, setOpenNotes] = useState({});
  /* THE ROW OPENS ONTO ITS SKUs — Max, 2026-09-19: "khi click vào sẽ expand ra danh sách SKU bên
     dưới rồi user sẽ xem project có những SKU nào rồi sẽ submit hay approve cho những SKU được
     chọn." Deciding per SKU was already possible in the dialog; what was missing is SEEING what is
     in a project before opening anything, and acting on part of it. */
  const [openSkus, setOpenSkus] = useState({});     // groupId -> true
  const [pickedSkus, setPickedSkus] = useState({}); // projectId -> true
  const toggleSkuPanel = (gid) => setOpenSkus((o) => ({ ...o, [gid]: !o[gid] }));
  /* THE TICKS ARE THE SELECTION — Max, 2026-09-21: "khi tôi bỏ tick tất cả SKU thì project vẫn
     hiện và nút submit vẫn work á". Ticking a project ticks its items, so a project standing ticked
     over nothing was the row claiming a selection its own list had just emptied — and the toolbar,
     which reads the project ticks, went on offering to submit and to decide it.
     A project is ticked exactly while something inside it is. Unticking the last item takes the
     project off with it; ticking the first puts it back. */
  const toggleSku = (pid) => {
    const on = !pickedSkus[pid];
    const next = { ...pickedSkus, [pid]: true };
    if (!on) delete next[pid];
    setPickedSkus(next);
    const g = rows.find((x) => (x.skuRows || []).some((r) => r.projectId === pid));
    if (!g) return;
    const any = skusOf(g.groupId).some((id) => next[id]);
    setSelected((prev) => (any
      ? (prev.includes(g.groupId) ? prev : [...prev, g.groupId])
      : prev.filter((x) => x !== g.groupId)));
  };
  const pickedOf = (g) => (g.skuRows || []).filter((r) => pickedSkus[r.projectId]);

  const [editFor, setEditFor] = useState(null);     // group being corrected after a rejection
  // A save that fails server-side used to land in the page banner, which the modal overlay covers —
  // the user saw a sliver of red text behind the dialog and no way to read it (Max, 2026-08-13).
  const [editError, setEditError] = useState('');
  // Option lists for the SKU cards. Same source the tracker uses; loaded lazily, because the
  // approval queue itself does not need them.
  const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER_OPTIONS);
  // Full SKU rows (not just their names) so the read-only view can expand each SKU the same way
  // the New Project form does. Comes from the tracker bootstrap, loaded with the option lists.
  const [skuRows, setSkuRows] = useState([]);
  // Excel-style column tools + multi-select, one set per tab (they hold different rows).
  const [sort, setSort] = useState({ key: '', dir: 'asc' });
  const [colFilters, setColFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);   // { key, anchor }
  const [selected, setSelected] = useState([]);         // groupIds ticked for a bulk decision
  const [bulkBusy, setBulkBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  // Jay item 8: open on the viewer's own team, with a control to widen it. '' = every team.
  const [teamScope, setTeamScope] = useState('');
  const [search, setSearch] = useState('');
  /* A BAR OF THE CHARTS, PRESSED — Max, 2026-10-20: "khi click vào thì phía dưới sẽ hiện danh sách
     project tương ứng nhé". { key, label, ids } from the chart; the table below is the Approved tab
     narrowed to exactly those projects, and a chip over it names the bar and takes it off again. */
  const [chartPick, setChartPick] = useState(null);
  const teamScopeApplied = useRef(false);
  const { functions: authFunctions, permissions: authPerms, roles: authRoles, activeRole, loading: roleLoading } = useAuth();
  const has = (key) => Array.isArray(authFunctions) && authFunctions.includes(key);
  // Max, 2026-08-17: Admin may edit straight from the Pending queue — a migrated project often
  // reaches the queue missing its Team / Owner / naming parts, and the alternative is hunting it
  // down in the tracker. Mirrors how `has()` treats the header's role switcher: when one role is
  // active only that role counts, otherwise the union of the roles the user holds.
  const isAdmin = activeRole ? activeRole === 'Admin' : (Array.isArray(authRoles) && authRoles.includes('Admin'));

  const openProject = async (group, mode) => {
    setEditFor({ group, mode });
    setEditError('');
    if (filterOptions !== EMPTY_FILTER_OPTIONS) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/development/sample-tracker/bootstrap`, { credentials: 'include' });
      if (!res.ok) return;
      const boot = await res.json();
      setSkuRows(boot.projects || []);
      const lists = boot.lists || {};
      const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort();
      setFilterOptions({
        assignees: uniq((boot.projects || []).map((p) => p.assignedTo)),
        skuDevelopers: lists.skuDevelopers?.length ? lists.skuDevelopers : uniq((boot.projects || []).map((p) => p.assignedTo)),
        skuDesigners: lists.skuDesigners?.length ? lists.skuDesigners : uniq((boot.projects || []).map((p) => p.designerName)),
        allocations: lists.allocations || [],
        projectCategories: lists.projectCategories || [],
        projectStatuses: lists.projectStatuses || ['On Track', 'Concern', 'Delayed', 'On Hold', 'Ship Finished', 'Complete', 'Dropped'],
        projectTypologies: lists.projectTypologies || [],
        projectStageStatuses: lists.projectStageStatuses || ['Pending', 'Complete', 'Delayed', 'On Hold'],
        projectProductTypes: lists.projectProductTypes || [],
      });
    } catch {
      /* the form still works; the SKU dropdowns just stay empty */
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [approvals, cfg] = await Promise.all([api('/approvals'), api('/config')]);
      setQueue({ pending: approvals.pending, approved: approvals.approved, rejected: approvals.rejected });
      /* Counted on the server: chart (b) is about EVERY approved project and this screen is only
         ever sent the approver's own, 53 of 112 today. */
      setCharts(approvals.charts || null);
      setApproverName(approvals.approverName || '');
      setMe(approvals.me || null);
      // Applied once, right after the first load — re-applying would override the user every time
      // they widened the scope themselves.
      if (!teamScopeApplied.current && approvals.me?.teamKey) {
        teamScopeApplied.current = true;
        setTeamScope(approvals.me.teamKey);
      }
      setConfig(cfg);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Status banners disappear on their own — same behaviour as the tracker screens.
  useEffect(() => {
    if (!message && !error) return undefined;
    const id = setTimeout(() => { setMessage(''); setError(''); }, 20000);
    return () => clearTimeout(id);
  }, [message, error]);

  // Adding a Partner / Line from inside the form, same as the tracker does.
  const addPartner = async (name) => {
    try {
      const data = await api('/partners', { method: 'POST', body: JSON.stringify({ name }) });
      const cfg = await api('/config');
      setConfig(cfg);
      return (cfg.partners || []).find((p) => p.partnerId === data.partnerId) || null;
    } catch (e) {
      setError(e.message);
      return null;
    }
  };

  const addSalesRep = async (name) => {
    try {
      const data = await api('/sales-reps', { method: 'POST', body: JSON.stringify({ name }) });
      const cfg = await api('/config');
      setConfig(cfg);
      return (cfg.salesReps || []).find((r) => r.salesRepId === data.salesRepId) || null;
    } catch (e) {
      setError(e.message);
      return null;
    }
  };

  // Edit is what makes the queue actionable for a manager: on Rejected they fix what the CEO
  // objected to and send it straight back; on Pending they fill in the Team / Owner / naming a
  // migrated project never had. Either way it beats hunting the project down in the tracker.
  // Status-agnostic on purpose — PUT /groups/:id does not care which queue the group sits in.
  const saveEdit = async (group, patch) => {
    setBusyId(group.groupId);
    setError('');
    setEditError('');
    try {
      const { skus = [], editedSkus = [], ...fields } = patch;
      /* THE GROUP GOES LAST — Thuan, 2026-10-05, through Max. It used to go first, and changing the
         project owner in the same save is what exposed why that is wrong: the group PUT wrote the
         new owner, and the per-SKU PUTs that followed were then judged against it, so the person
         who had just pressed Save was no longer the owner and every SKU came back 403.
         What he saw was the worst possible version of it — an error saying the SKU is somebody
         else's, and then, on a refresh, the new owner sitting there anyway. Max: "nhìn như hệ
         thống bị lỗi á". Half the save had gone through and the screen reported the half that had
         not.
         The group update is the ONLY step that can take the caller's rights away, so it is the only
         one whose order matters: run it last and everything else is still judged against the person
         who opened the form. */
      /* THE SAME RIGHTS THE WRITES WILL BE JUDGED BY, asked once for all of them — Max,
         2026-10-05. A person can be allowed to edit some SKUs of a project and not others, so
         asking row by row is what let a refusal on the third one leave the first two saved. */
      await api(`/groups/${encodeURIComponent(group.groupId)}/precheck`, {
        method: 'POST',
        body: JSON.stringify({ projectIds: editedSkus.map((sk) => sk.projectId).filter(Boolean) }),
      });
      // Existing SKUs the user touched. PUT /projects/:id takes the tracker's own project shape, so
      // each card is mapped back onto the row it came from — sending only the card's fields would
      // blank everything the card does not show (stage history, linked samples, source columns).
      for (const sku of editedSkus) {
        const original = skuRows.find((p) => p.id === sku.projectId);
        if (!original) continue;
        // eslint-disable-next-line no-await-in-loop
        await api(`/projects/${encodeURIComponent(sku.projectId)}`, {
          method: 'PUT',
          body: JSON.stringify({
            ...original,
            projectItem: String(sku.item || '').trim(),
            subItem: String(sku.item || '').trim(),
            productType: sku.itemCategory || '',
            typology: sku.typology || '',
            frameColor: sku.frameColor || '',
            assignedTo: sku.assigned || '',
            designerName: sku.designer || '',
            allocation: sku.allocation || '',
            status: sku.status || original.status,
            startDate: sku.startDate || '',
            exfShipDate: String(sku.exfDate || '') === 'TBA' ? '' : (sku.exfDate || ''),
            estimateQty: String(sku.qty || '') === 'TBA' ? '' : (sku.qty || ''),
            targetPrice: sku.targetPrice ?? '',
            launchDate: sku.launchDate || '',
            remark: sku.remark || '',
            sampleId: sku.samples || '',
            ...(sku.stages || {}),
            /* The server reads a SKU's stages from `stages` and from nowhere else; spread flat (as
               above, which the grid's row shape needs) they were ignored, so every save from here
               put L/D S/OFF, Costing, Hand-off and the rest back to Pending — and an edit typed into
               a stage box was never saved. Found 2026-10-09 while adding the repeat-order tick. */
            stages: sku.stages || {},
            // The repeat-order tick travels only when it was touched — see RepeatOrderField.
            ...(sku.repeatOrderSet ? { repeatOrder: !!sku.repeatOrder, repeatOrderSet: true } : {}),
          }),
        });
      }
      // New SKU cards become project rows under this group — the same shape /projects/batch builds.
      for (const sku of skus) {
        // eslint-disable-next-line no-await-in-loop
        await api('/projects', {
          method: 'POST',
          body: JSON.stringify({
            projectItem: String(sku.item || '').trim(),
            subItem: String(sku.item || '').trim(),
            category: fields.category,
            productType: sku.itemCategory || '',
            typology: sku.typology || '',
            frameColor: sku.frameColor || '',
            assignedTo: sku.assigned || '',
            designerName: sku.designer || '',
            allocation: sku.allocation || '',
            status: sku.status || 'On Track',
            startDate: sku.startDate || '',
            exfShipDate: String(sku.exfDate || '') === 'TBA' ? '' : (sku.exfDate || ''),
            estimateQty: String(sku.qty || '') === 'TBA' ? '' : (sku.qty || ''),
            targetPrice: sku.targetPrice || '',
            launchDate: sku.launchDate || '',
            remark: sku.remark || '',
            sampleId: sku.samples || '',
            stages: sku.stages || {},
            repeatOrder: !!sku.repeatOrder,
            groupId: group.groupId,
          }),
        });
      }
      await api(`/groups/${encodeURIComponent(group.groupId)}`, { method: 'PUT', body: JSON.stringify(fields) });
      setMessage(skus.length
        ? t('xPa.updatedSkus', { id: group.groupId, n: skus.length })
        : t('xPa.updated', { id: group.groupId }));
      setEditFor(null);
      await load();
    } catch (e) {
      setEditError(e.message);
    } finally {
      setBusyId('');
    }
  };

  const decide = async (group, decision, note, decisions) => {
    setBusyId(group.groupId);
    setError('');
    try {
      /* `decisions` is one row per SKU — Jay's three outcomes are what they add up to, so the URL's
         approve/reject only decides which endpoint is called and the rows say what happened. A call
         without them is the bulk path and the older clients, and still means "this decision, for
         every SKU still waiting". */
      const r = await api(`/approvals/${encodeURIComponent(group.groupId)}/${decision}`, {
        method: 'POST',
        body: JSON.stringify({ note: note || '', ...(decisions ? { decisions } : {}) }),
      });
      /* Say where the mail went. It used to be obvious — the creator and the owner. Since 2026-08-20
         it is the team's group mailbox, and a wrong address on a team would otherwise go unnoticed for
         weeks. The resubmit and bulk paths already reported this; the single decision did not. */
      const mailNote = r?.mail?.ok ? ` — ${t('xPa.mailSentTo', { to: r.mail.to })}`
        : r?.mail?.skipped ? ` — ${t('xPa.mailSkipped', { reason: r.mail.skipped })}`
          : r?.mail?.error ? ` — ${t('xPa.mailFailed', { error: r.mail.error })}` : '';
      const roll = r?.skuApproval;
      const id = group.groupId;
      const what = roll && roll.total
        ? (roll.rejected === 0 ? t('xPa.decApprovedN', { id, n: roll.approved })
          : roll.approved === 0 ? t('xPa.decRejectedN', { id, n: roll.rejected })
            : t('xPa.decPartly', { id, a: roll.approved, r: roll.rejected }))
        : (decision === 'approve' ? t('xPa.decApproved', { id }) : t('xPa.decRejected', { id }));
      setMessage(`${what}${mailNote}.`);
      setDecisionFor(null);
      setDecisionNote('');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId('');
    }
  };

  /* Send only the items that were ticked. The endpoint takes `skuIds` and stamps exactly those,
     so a project with two items ready and three still being costed no longer has to wait. */
  const submitSkus = async (group, skus) => {
    setBusyId(group.groupId);
    setError('');
    try {
      const r = await api('/approvals/submit', {
        method: 'POST',
        body: JSON.stringify({ groupIds: [group.groupId], skuIds: skus.map((x) => x.projectId) }),
      });
      const sent = (r?.skus || []).length;
      setMessage(t('xPa.skusSent', { id: group.groupId, n: sent })
        + (r?.mail?.ok ? ` — ${t('xPa.mailSentTo', { to: r.mail.to })}` : ''));
      setPickedSkus({});
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId('');
    }
  };

  const resubmit = async (group, note, skuNotes = {}) => {
    setBusyId(group.groupId);
    setError('');
    try {
      const r = await api(`/approvals/${encodeURIComponent(group.groupId)}/resubmit`, {
        method: 'POST',
        // Only the ticked items when any were ticked; the server falls back to every refused one.
        body: JSON.stringify({
          note: note || '',
          ...(group.resubmitSkus?.length ? { skuIds: group.resubmitSkus } : {}),
          ...(Object.keys(skuNotes).length ? { skuNotes } : {}),
        }),
      });
      const n = (r.skus || []).length;
      setMessage((n ? t('xPa.resubmittedN', { id: group.groupId, n }) : t('xPa.resubmitted', { id: group.groupId }))
        + `${r.mail?.ok ? ` — ${t('xPa.mailSentTo', { to: r.mail.to })}` : ''}.`);
      setDecisionFor(null);
      setDecisionNote('');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId('');
    }
  };

  // Team -> "A, B": every active member flagged as a team manager. Falls back to the team's manager
  // pointer so a team whose members are not flagged yet still shows a name.
  const managersByTeam = useMemo(() => {
    const map = {};
    (config?.teams || []).forEach((tm) => {
      const flagged = (tm.members || []).filter((m) => m.isTeamManager).map((m) => m.name).filter(Boolean);
      const names = flagged.length ? flagged : [tm.managerName].filter(Boolean);
      map[tm.teamKey] = names.join(', ');
    });
    return map;
  }, [config]);

  /* THE CEO SEES WHAT HAS BEEN ASKED OF HIM — Jay, 2026-10-13, on a screenshot taken in that role:
     "Please only let Young sees requested ones, not pending requests". A project on Pending that
     nobody has sent him yet (every SKU still "not sent") is the team's move, not his: four of the ten
     rows on Jay's screen were that, drawn with no Approve and nothing to do. In the CEO role Pending
     keeps only projects with at least one SKU sent and still waiting — the same test that draws his
     Approve button — and the tab's count follows. The rule Pricing Approval keeps for him since
     2026-09-17, and like it a VIEW read from useAuth (which follows the header's role switcher): who
     may decide is unchanged and the server enforces it either way. Not known yet is not "not the
     CEO" (the 2026-09-17 flash): until the role list has arrived the queue is not drawn. */
  const actingAsCeo = activeRole ? activeRole === 'CEO'
    : (Array.isArray(authRoles) && authRoles.length === 1 && authRoles[0] === 'CEO');
  const askedOfHim = (g) => (Array.isArray(g.skuRows) ? g.skuRows : [])
    .some((r) => (r.approvalStatus || 'Pending') === 'Pending' && r.requestedAt);
  const shownQueue = useMemo(() => {
    if (roleLoading) return {};
    if (!actingAsCeo) return queue;
    return { ...queue, pending: (queue.pending || []).filter(askedOfHim) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue, actingAsCeo, roleLoading]);

  // Stamped here rather than in the cell so sorting and the column filter menu see the same value.
  const rawRows = useMemo(() => (shownQueue[tab] || []).map((r) => ({
    ...r, managerNames: managersByTeam[r.teamKey] || '',
  })), [shownQueue, tab, managersByTeam]);
  const columns = useMemo(() => [...APPROVAL_COLUMNS, ...(APPROVAL_EXTRA[tab] || [])], [tab]);
  const getters = useMemo(() => Object.fromEntries(columns.map((c) => [c.key, c.get])), [columns]);
  const rows = useMemo(() => {
    // A project with no team yet belongs to nobody — it stays visible in every scope, otherwise the
    // 97 waiting for Jay's assignment would vanish from the queue entirely.
    /* An unassigned project answers to no team, so it stays out of a team's list — the same rule the
       tracker's Team filter follows (Max, 2026-09-03). It is still on All teams. */
    /* A pressed bar decides the list on its own: the team is in the bar already, and a team scope
       left on the other team would show an empty table under a bar that says 31. */
    const picked = chartPick && tab === 'approved' ? new Set(chartPick.ids) : null;
    const scoped = picked ? rawRows.filter((r) => picked.has(r.groupId))
      : (teamScope ? rawRows.filter((r) => r.teamKey === teamScope) : rawRows);
    const q = searchNorm(search.trim());
    const searched = q ? scoped.filter((r) => rowMatches(r, q)) : scoped;
    const filtered = Object.keys(colFilters).length
      ? searched.filter((r) => rowPassesColumnFilters(r, colFilters, getters))
      : searched;
    return sortRows(filtered, sort, getters);
  }, [rawRows, colFilters, sort, getters, teamScope, search, chartPick, tab]);

  // Switching tab resets the column tools and the selection — the rows underneath are different.
  const switchTab = (key) => {
    setTab(key);
    if (key !== 'approved') setChartPick(null);
    setSort({ key: '', dir: 'asc' });
    setColFilters({});
    setOpenFilter(null);
    setSelected([]);
  };

  const thProps = (key) => ({
    sortKey: key,
    sort,
    onSort: (k) => setSort((p) => (p.key === k ? { key: k, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'asc' })),
    filterValues: openFilter?.key === key ? distinctColumnValues(rawRows, key, colFilters, getters) : undefined,
    filterSelected: colFilters[key],
    filterOpen: openFilter?.key === key,
    filterAnchor: openFilter?.key === key ? openFilter.anchor : null,
    onOpenFilter: (k, anchor) => setOpenFilter((p) => (p && p.key === k ? null : { key: k, anchor })),
    onCloseFilter: () => setOpenFilter(null),
    onApplyFilter: (k, values, allValues) => {
      setColFilters((prev) => {
        const next = { ...prev };
        if (!values || !values.length || values.length === allValues.length) delete next[k];
        else next[k] = values;
        return next;
      });
      setOpenFilter(null);
    },
  });

  // Same reason as the tracker's New Project button: useAuth respects the header's role switcher,
  // the API's `me` always answers with the union of every role. The API still enforces the real grant.
  // Same rule the server enforces on PUT /groups/:id. Showing it on the button means a manager
  // learns the project is not theirs before filling the form, instead of after pressing Save
  // (Max, 2026-08-13).
  const mayEditGroup = (g) => {
    if (!me) return true;
    if (me.isAdmin) return true;
    if (g.ownerUserId && Number(g.ownerUserId) === Number(me.userId)) return true;
    if (!g.teamKey && !g.ownerUserId) return true;      // never assigned — open, as on the server
    // Manager of THAT team — a person can be in several departments, managing only some of them.
    return !!(g.teamKey && (me.managerTeams || []).includes(g.teamKey));
  };

  /* Deleting a whole project is narrower than editing it: an admin, or the MANAGER of the owning
     team (Max, 2026-09-02). No owner branch and no "unassigned is open" branch — the server has
     neither, and a button that offers something the API refuses is worse than no button.
     An admin is not team-scoped: both Sales OP teams are theirs to delete from. */
  const mayDeleteGroup = (g) => {
    if (!me) return false;
    if (me.isAdmin) return true;
    return !!(g.teamKey && (me.managerTeams || []).includes(g.teamKey));
  };

  /* The submitted freeze only lasts while the approver is still holding the decision. Once he has
     rejected it the project is back with the team and there is nothing left to pull out from under
     him — which is the line the server's DELETE already draws (approval_notified_at AND
     approval_status <> 'Rejected'). Reading the status here, not the tab, keeps the button and the
     API on the same rule. Max, 2026-09-14: "Bên rejected thiếu tính năng xóa á". */
  const deleteFrozen = (g) => !!g.submittedAt && g.approvalStatus !== 'Rejected';

  /* One Delete button, rendered on Pending and on Rejected. Drawn for anyone who could ever delete
     and disabled per row with the reason on it, the same shape as Edit: dropping it from rows that
     refuse it would change the slot count from row to row, and the Action column is sized once per
     tab from that count. */
  /* REFUSED ITEMS ARE SENT BACK FROM WHEREVER THEY ARE SHOWN — Jay via Max, 2026-09-21: a rejected
     item belongs in Pending and its button reads Resubmit. Rendered by BOTH tabs from here, because
     two copies of one button is how it comes to behave differently depending on where it was
     pressed.
     It stands BESIDE Submit rather than replacing it. Measured today: no project holds both an
     unsent and a refused item, but nothing prevents one — and then a single button would be sending
     two different sets of items under one word. */
  const refusedOf = (g) => (g.skuRows || []).filter((r) => r.approvalStatus === 'Rejected');
  const resubmitButton = (g) => {
    /* The same rule as Submit and Approve: the ticks say how much of the project this is about.
       Nothing ticked means every refused item, which is what this button has always done. */
    const back = pickedOf(g).filter((r) => r.approvalStatus === 'Rejected');
    return (
      <button
        type="button"
        className="pa-btn"
        title={mayEditGroup(g) ? '' : t(NOT_YOUR_TEAM_KEY)}
        disabled={busyId === g.groupId || !mayEditGroup(g)}
        onClick={() => {
          setDecisionFor({
            mode: 'single',
            action: 'resubmit',
            group: back.length ? { ...g, resubmitSkus: back.map((r) => r.projectId) } : g,
          });
          setDecisionNote('');
        }}
      >
        <RotateCcw size={14} /> {back.length ? t('pa.resubmitSome', { n: back.length }) : t('pa.btnResubmit')}
      </button>
    );
  };

  const dropButton = (g) => (
    <button
      type="button"
      className="pa-btn pa-btn-drop"
      title={deleteFrozen(g) ? t('pa.dropSubmitted')
        : (!mayDeleteGroup(g) ? t(NOT_YOUR_TEAM_KEY) : t('pa.dropTitle'))}
      disabled={busyId === g.groupId || deleteFrozen(g) || !mayDeleteGroup(g)}
      onClick={() => askDeleteGroup(g)}
    >
      <Trash2 size={14} /> {t('pa.drop')}
    </button>
  );

  /* An action grant is 'edit'; 'view' on one of these means "watch the queue", nothing more.
     SOP Team holds project_tracker_approve at VIEW, which is what puts this screen in their menu —
     but testing for the KEY alone drew them an Approve button whose POST the server answers with a
     403 (Max, 2026-09-08: "ẩn 2 nút sent và approve ở role SOP team"). The server already writes
     this exact rule into `me.canApproveProject`; it is read from useAuth instead because only
     useAuth follows the header's role switcher, and `me` always answers with the union. */
  const canApprove = authPerms.project_tracker_approve === 'edit';
  // date change requests waiting (ProjectDates.jsx) — counted into the Pending tab, which shows them first
  const [dateChanges, setDateChanges] = useState(0);
  const canResubmit = authPerms.project_tracker_create === 'edit';

  /* Deleting a whole project — Max, 2026-09-02. Two steps, because the server's DELETE is two steps:
     a preflight says how many SKUs and which tickets go with it, then the real call acknowledges
     them. Refusing to delete a SUBMITTED project is his rule: the CEO has the mail, and pulling the
     project out from under it leaves them approving something that no longer exists. Completed SKUs
     are refused by the server as well, since Production has already been told. */
  const [dropGroup, setDropGroup] = useState(null);
  // A destructive call needs its own in-flight flag: without it the confirm button stayed live
  // and a second click sent a second DELETE for a project that was already gone.
  const [dropBusy, setDropBusy] = useState(false);
  const [dropReason, setDropReason] = useState('');
  const [dropErr, setDropErr] = useState('');

  const askDeleteGroup = async (g) => {
    setDropErr(''); setDropReason('');
    setDropGroup({ group: g, loading: true, skus: 0, tickets: [] });
    try {
      const data = await api(`/groups/${encodeURIComponent(g.groupId)}`, {
        method: 'DELETE', body: JSON.stringify({ preflight: true }),
      });
      setDropGroup({
        group: g, loading: false, skus: data.skus ?? 0, tickets: data.tickets || [],
      });
    } catch (err) {
      setDropGroup(null);
      setError(err.message);
    }
  };

  const doDeleteGroup = async () => {
    if (!dropGroup || dropGroup.loading || dropBusy) return;
    if (dropGroup.tickets.length && !dropReason.trim()) return;
    setDropErr('');
    setDropBusy(true);
    try {
      const data = await api(`/groups/${encodeURIComponent(dropGroup.group.groupId)}`, {
        method: 'DELETE',
        body: JSON.stringify({ acknowledgeTickets: true, reason: dropReason.trim() }),
      });
      const tickets = data.ticketsDeleted
        ? `, ${t('xPa.ticketsCancelled', { n: data.ticketsDeleted })}` : '';
      setMessage(t('xPa.groupDeleted', {
        id: dropGroup.group.groupId, skus: data.skusDeleted, samples: data.samplesDetached,
      }) + tickets);
      setDropGroup(null);
      load();
    } catch (err) { setDropErr(err.message); }
    finally { setDropBusy(false); }
  };

  const clearTools = () => { setSort({ key: '', dir: 'asc' }); setColFilters({}); setOpenFilter(null); setSearch(''); };
  // A fixed table layout is what makes the frozen columns line up: sticky offsets are hard numbers,
  // so the widths behind them cannot be left to the browser's auto layout.
  /* Tick boxes serve two different people: the approver ticks to decide in bulk, the manager ticks
     to submit in bulk. A manager has project_tracker_create but NOT approve, so gating on canApprove
     alone would leave them with a Submit-selected button and nothing to select. */
  /* THE REJECTED TAB HAS TICKS TOO — Max, 2026-09-21: "ko có nút tick trên dòng project… và nút
     tick ở project bị reject cũng ko work". Resubmit lives on that tab and it sends items back, so
     it needs the same way of saying which — the column was drawn for the other two tabs only. */
  /* A TICK ONLY WHERE THE TICKER CAN ACT. Approving happens on the queue, sending back on the
     Rejected tab — so a CEO was being offered a tick column on Rejected beside a row whose only
     button is View. A tick with no button behind it is the fault this screen has been correcting
     all day, one level up. */
  const showCheck = (canApprove && tab === 'pending')
    || ((canResubmit || isAdmin) && (tab === 'pending' || tab === 'rejected'));
  // Mirrors exactly what the action cell renders below, so the two can never disagree.
  const actionButtons = (() => {
    const names = ['view'];
    if (tab === 'pending') {
      if (isAdmin || canResubmit) names.push('edit', 'submit');
      /* A SECOND SLOT WHERE A ROW CARRIES BOTH. Submit and Remind are one slot in the width table
         because for most of a project's life only one of them exists; a part-submitted project has
         both, and this list has already hung a button outside the frozen column twice for a slot
         nobody counted. Mirrors the cell, which is what the note above demands. */
      if ((isAdmin || canResubmit) && rows.some((r) => r.submittedAt
          && (r.skuRows || []).some((x) => (x.approvalStatus || 'Pending') === 'Pending' && x.requestedAt)
          && (r.skuRows || []).some((x) => (x.approvalStatus || 'Pending') === 'Pending' && !x.requestedAt))) {
        names.push('submit');
      }
      if ((isAdmin || canResubmit) && rows.some((r) => refusedOf(r).length > 0)) names.push('resubmit');
      if (isAdmin || canResubmit) names.push('drop');
      if (canApprove) names.push('approve');
    } else if (tab === 'rejected') {
      // Mirrors the Rejected tab's cell: Edit and Resubmit for whoever may fix it, Delete last.
      if (isAdmin || canResubmit) names.push('edit', 'resubmit', 'drop');
    }
    return names;
  })();
  const actionW = actionWidthFor(actionButtons);
  const gridWidth = (showCheck ? CHECK_W : 0) + NO_W + actionW
    + columns.reduce((sum, c) => sum + (c.width || 120), 0);
  const toolsActive = !!sort.key || !!Object.keys(colFilters).length || !!search.trim();

  /* WHOSE MOVE IS THIS SKU — Max, 2026-09-20: "nếu đã waiting rùi thì hem cần tick chọn nữa nhưng
     hiện vẫn cho tick thì ko hợp lý".

     A tick is an offer to act, so it is only offered where there is something this person can do:
       not sent yet  the requester's move — they send it
       waiting       the APPROVER's move — the requester has already done their part
       decided       nobody's; it is over
     The same row is therefore tickable for Young and dead for Tina, which is the honest answer:
     they are not looking at the same question. */
  const skuActionable = (r) => {
    const st = r.approvalStatus || 'Pending';
    /* On the Rejected tab the question is "which of these do I send back", so only the REFUSED items
       are live there. On Pending a refused item is live too: it is back with the team that raised it,
       and Resubmit is how it goes out. */
    if (tab === 'rejected') return st === 'Rejected' && (isAdmin || canResubmit);
    if (st === 'Rejected') return isAdmin || canResubmit;
    if (st !== 'Pending') return false;
    return r.requestedAt ? canApprove : (isAdmin || canResubmit);
  };
  const skuWhyDead = (r) => {
    const st = r.approvalStatus || 'Pending';
    if (tab === 'rejected') return st === 'Rejected' ? t('pa.skuNotYours') : t('pa.skuNotRefused');
    if (st === 'Rejected') return t('pa.skuNotYours');
    if (st !== 'Pending') return t('pa.skuDoneAlready');
    return r.requestedAt ? t('pa.skuWithApprover') : t('pa.skuNotYours');
  };
  /* WHAT IS LEFT TO SEND, asked of the ITEMS and not of the group's own stamp. `submittedAt` is
     the group's approval_notified_at and it is set the moment ONE item goes, so gating Submit on it
     took the button away from a project 2 items into 5 and left the other three with no way out —
     the same fault the server's submit guard had, and for the same reason. */
  const stillToSend = (g) => (Array.isArray(g.skuRows) ? g.skuRows : [])
    .filter((r) => (r.approvalStatus || 'Pending') === 'Pending' && !r.requestedAt);
  // The same question from the other side: an Approve button on a project nobody has sent him yet
  // opens a dialog with nothing in it.
  const stillToDecide = (g) => (Array.isArray(g.skuRows) ? g.skuRows : [])
    .filter((r) => (r.approvalStatus || 'Pending') === 'Pending' && r.requestedAt);

  // Ticking a project ticks what this person can act on, and nothing else.
  const skusOf = (gid) => ((rows.find((x) => x.groupId === gid)?.skuRows) || [])
    .filter(skuActionable).map((r) => r.projectId);
  const setSkuPicks = (gid, on) => setPickedSkus((prev) => {
    const next = { ...prev };
    skusOf(gid).forEach((pid) => { if (on) next[pid] = true; else delete next[pid]; });
    return next;
  });

  // Selection only ever covers the rows currently on screen, so a hidden filtered-out project can
  // never be approved by accident.
  const visibleIds = rows.map((r) => r.groupId);
  const selectedVisible = selected.filter((id) => visibleIds.includes(id));
  /* A project with nothing in it for this person is not part of "all". The header box would
     otherwise tick it, tick none of its items, and leave it standing in the toolbar's count. */
  const actionableIds = visibleIds.filter((gid) => skusOf(gid).length);
  const allSelected = !!actionableIds.length && selectedVisible.length === actionableIds.length;
  /* TICKING A PROJECT TICKS ITS ITEMS — Max, 2026-09-20: "khi tôi tick 1 dòng project thì phải mở
     ra các SKU và trong trạng thái được tick luôn nhé. Nếu user bỏ tick ở dòng project thì các SKU
     cũng đồng loạt bỏ tick."

     The two selections are not separate things any more: since the decision moved down to the SKU,
     ticking a project MEANS ticking what is in it, and the panel opens so the person can see what
     they have just agreed to act on rather than being told a count. Unticking puts every one of
     them back, so the box is a true toggle and not a one-way switch. */
  // `queue` is {pending, approved, rejected}, not a list — the rows on screen are what the boxes
  // act on, and they are the ones that carry skuRows.
  const toggleAll = () => {
    const on = !allSelected;
    setSelected(on ? actionableIds : []);
    actionableIds.forEach((gid) => setSkuPicks(gid, on));
    /* THE HEADER OPENS THEM ALL — Max, 2026-09-20: "tick ở header thì expand ra hết các project
       nhé". I had left it ticking the items without opening the panels, on the grounds that fifty
       open panels is a wall; he ruled the other way, and the rule he is holding to is the plainer
       one — the box means the same thing wherever it is.

       Unticking CLOSES them again, which is the one place this differs from a single row. The
       header tick is what opened fifty panels, so the header untick is its undo; leaving them open
       would mean shutting fifty of them one at a time. */
    setOpenSkus((o) => {
      const next = { ...o };
      actionableIds.forEach((gid) => { if (on) next[gid] = true; else delete next[gid]; });
      return next;
    });
  };
  const toggleOne = (id) => {
    const on = !selected.includes(id);
    setSelected((prev) => (on ? [...prev, id] : prev.filter((x) => x !== id)));
    setSkuPicks(id, on);
    if (on) setOpenSkus((o) => ({ ...o, [id]: true }));
  };

  /* Submit to the approver — one project or every ticked one (Young via Max, 2026-08-21).
     The server decides what is allowed (own team, Pending, not already submitted) and refuses the
     rest by name, so the button does not have to be clever and cannot be talked into more than the
     manager may do. */
  const submitForApproval = async (ids) => {
    if (!ids.length) return;
    setSubmitBusy(true);
    setError(''); setMessage('');
    try {
      /* THE TOOLBAR AND THE ROW HAVE TO AGREE — 2026-09-20. Ticking a project ticks its items, so
         the two buttons cover the same ground; untick two of the five afterwards and a toolbar that
         still sent all five would be the row saying "Submit 3" while the thing beside it sent 5.
         So it sends the ticked ITEMS whenever any are ticked, and whole projects otherwise. */
      const picks = ids.flatMap((gid) => (rows.find((x) => x.groupId === gid)?.skuRows || [])
        .filter((r) => pickedSkus[r.projectId]
          && (r.approvalStatus || 'Pending') === 'Pending' && !r.requestedAt)
        .map((r) => r.projectId));
      const r = await api('/approvals/submit', {
        method: 'POST',
        body: JSON.stringify({ groupIds: ids, ...(picks.length ? { skuIds: picks } : {}) }),
      });
      const sent = (r.submitted || []).length;
      const skuN = (r.skus || []).length;
      setMessage((skuN ? t('xPa.projectsSubmittedSkus', { count: sent, skus: skuN }) : t('xPa.projectsSubmitted', { count: sent }))
        + ` — ${t('xPa.requestSentTo', { to: r.mail?.to || t('xPa.theApprover') })}`
        + ((r.refused || []).length ? ` · ${t('xPa.notSent', { list: r.refused.join('; ') })}` : ''));
      setPickedSkus({});
      setSelected([]);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitBusy(false);
    }
  };

  /* REMIND — Max, 2026-10-02: "cần thêm nút remind á và tôi nghĩ nó sẽ là khi User bấm submit thì
     thay vì đổi thành sent thì sẽ đổi thành nút remind á". It posts to the SUBMIT endpoint with
     `remind`, because the mail is the same mail built from the same items; what differs is which
     items it is about and that nothing is re-stamped.
     NO TICKS. Submit asks which of five to send because the rest are still being costed; a reminder
     is about everything already sitting with the approver, and offering to chase two of the three
     he is holding would be a second axis nobody asked for. */
  const remindApproval = async (g) => {
    setSubmitBusy(true);
    setError(''); setMessage('');
    try {
      const r = await api('/approvals/submit', {
        method: 'POST',
        body: JSON.stringify({ groupIds: [g.groupId], remind: true }),
      });
      const skuN = (r.skus || []).length;
      setMessage(t('pa.remindSent', { n: skuN, to: r.mail?.to || t('xPa.theApprover') })
        + ((r.refused || []).length ? ` · ${r.refused.join('; ')}` : ''));
      // Deliberately no reload: a reminder changes nothing on the row, and reloading the list here
      // would only throw the reader back to the top of a table they were reading.
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitBusy(false);
    }
  };

  const bulkDecide = async (decision, note) => {
    setBulkBusy(true);
    setError('');
    try {
      /* AND THE TOOLBAR DECIDES WHAT IS TICKED, the same rule Submit beside it already follows.
         Ticking a project ticks its items; untick two of four and a toolbar that still answered
         all four would be the row saying "Decide 2" while the button above it decided 4. */
      const picks = selectedVisible.flatMap((gid) => (rows.find((x) => x.groupId === gid)?.skuRows || [])
        .filter((r) => pickedSkus[r.projectId]
          && (r.approvalStatus || 'Pending') === 'Pending' && r.requestedAt)
        .map((r) => r.projectId));
      const r = await api('/approvals/bulk', {
        method: 'POST',
        body: JSON.stringify({
          groupIds: selectedVisible, decision, note: note || '',
          ...(picks.length ? { skuIds: picks } : {}),
        }),
      });
      const mailNote = r.mail?.ok ? ` · ${t('xPa.emailsSent', { count: r.mail.mails })}` : '';
      // What the server left alone is SAID. A count of 2 out of the 3 that were ticked, with
      // nothing explaining the third, is the screen deciding for the reader that it did not matter.
      const skipped = (r.skipped || []).length ? ` · ${t('xPa.notDecided', { list: r.skipped.join('; ') })}` : '';
      setMessage(`${decision === 'reject' ? t('xPa.bulkRejected', { count: r.count }) : t('xPa.bulkApproved', { count: r.count })}${mailNote}${skipped}.`);
      setSelected([]);
      setDecisionFor(null);
      setDecisionNote('');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="pa-page">
      <Header />
      <main className="pa-main">
        <div className="mh-titlerow">
          <div className="pa-title">
            <h2>{t('pa.title')}</h2>
            <p>{t('pa.subtitle')}</p>
          </div>
          <ModuleHelp manual="project" steps={TOUR} tourKey="projectApproval" ready={!loading} onBeforeTour={() => switchTab('pending')} />
        </div>


        {error && <div className="pa-banner err">{error}</div>}
        {message && <div className="pa-banner ok">{message}</div>}

        <div className="pa-tabrow">
        <div className="pa-tabs view-tabs" role="tablist">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              type="button"
              className={`pa-tab ${tab === tb.key ? 'active' : ''}`}
              onClick={() => switchTab(tb.key)}
            >
              <TabIcon k={tb.key} />{t(tb.label)}
              <span className={`pa-count${tb.key === 'rejected' && (shownQueue[tb.key] || []).length ? ' alert' : ''}`}>
                {(shownQueue[tb.key] || []).length + (tb.key === 'pending' ? dateChanges : 0)}
              </span>
            </button>
          ))}
          {has('role_management') && (
            <button
              type="button"
              className={`pa-tab ${tab === 'settings' ? 'active' : ''}`}
              onClick={() => switchTab('settings')}
            >
              <TabIcon k="settings" />{t('pa.tabSettings')}
            </button>
          )}
        </div>
        {tab !== 'settings' && (
          <ApprovalCharts
            charts={charts}
            approverName={approverName}
            pick={chartPick?.key || ''}
            onPick={(p) => {
              /* Every bar counts APPROVED projects, so its list is the Approved tab — opened with
                 nothing else narrowing it, or the table would hold fewer rows than the bar says. */
              if (p) {
                switchTab('approved');
                setSearch('');
              }
              setChartPick(p);
            }}
          />
        )}
        </div>

        {loading && <div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('pa.loading')}</div>}

        {!loading && tab === 'settings' && has('role_management') && (
          <SettingsPanel config={config} onSaved={(msg) => { setMessage(msg); load(); }} onError={setError} />
        )}

        {/* DATE CHANGE REQUESTS first on Pending — everything waiting for the CEO on one screen (Jay 28/10). */}
        {!loading && tab === 'pending' && <DateChangeList canDecide={canApprove} onCount={setDateChanges} />}

        {!loading && tab !== 'settings' && (
          <div className="pa-section">
            {/* Bulk bar: appears only when something is ticked, so the normal view stays quiet. */}
            <div className="pa-toolbar">
              <span className="pa-count-text">
                {rows.length === rawRows.length
                  ? t('pa.countAll', { n: rows.length })
                  : t('pa.countOf', { n: rows.length, total: rawRows.length })}
              </span>
              {chartPick && tab === 'approved' && (
                <span className="pa-chartpick" data-testid="pa-chartpick">
                  {t('pa.chartPick', { label: chartPick.label, n: chartPick.ids.length })}
                  <button type="button" onClick={() => setChartPick(null)} title={t('pa.chartPickClear')} aria-label={t('pa.chartPickClear')}>
                    <X size={13} />
                  </button>
                </span>
              )}
              <select className="pa-scope" value={teamScope} onChange={(e) => setTeamScope(e.target.value)} title={t('pa.scopeHint')}
                disabled={!!(chartPick && tab === 'approved')}>
                <option value="">{t('pa.allTeams')}</option>
                {(config?.teams || []).map((tm) => (
                  <option key={tm.teamKey} value={tm.teamKey}>{tm.teamName}</option>
                ))}
              </select>
              <input
                type="search"
                className="pa-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('pa.searchPh')}
                aria-label={t('pa.searchPh')}
              />
              {toolsActive && (
                <button type="button" className="pa-btn" onClick={clearTools}>{t('pa.clearTools')}</button>
              )}
              {/* The manager's side of the same tick boxes: submit what is ticked and not yet sent. */}
              {tab === 'pending' && (isAdmin || canResubmit) && !!selectedVisible.length && (
                <div className="pa-bulk">
                  {(() => {
                    const ready = selectedVisible.filter((id) => {
                      const g = rawRows.find((x) => x.groupId === id);
                      return g && !g.submittedAt && mayEditGroup(g);
                    });
                    return (
                      <>
                        <strong>{t('pa.readyToSubmit', { n: ready.length, total: selectedVisible.length })}</strong>
                        <button
                          type="button"
                          className="pa-btn submit"
                          disabled={submitBusy || !ready.length}
                          onClick={() => submitForApproval(ready)}
                          title={ready.length ? '' : t('pa.nothingToSubmit')}
                        >
                          <Mail size={14} /> {submitBusy ? t('pa.working') : t('pa.submitSelected')}
                        </button>
                      </>
                    );
                  })()}
                </div>
              )}
              {/* Deciding belongs on the QUEUE. On the Rejected tab the ticked items are refused,
                  not waiting, so this bar would open a dialog about nothing and report "0 projects
                  approved" — it was offered there only because the tab had no ticks before today. */}
              {!!selectedVisible.length && canApprove && tab === 'pending' && (
                <div className="pa-bulk">
                  <strong>{t('pa.selected', { n: selectedVisible.length })}</strong>
                  <button
                    type="button"
                    className="pa-btn approve"
                    disabled={bulkBusy}
                    onClick={() => {
                      /* ONE PROJECT IS NOT A BATCH — 2026-09-21. The bulk dialog has no per-SKU
                         list, so ticking a single project and pressing this gave a whole-group
                         decision while the Approve button on that very row opened the item list:
                         two buttons, two behaviours, one project. With one selected it opens the
                         same dialog the row does. */
                      const only = selectedVisible.length === 1
                        ? rows.find((x) => x.groupId === selectedVisible[0]) : null;
                      setDecisionFor(only
                        ? { mode: 'single', action: 'approve', group: only }
                        : { mode: 'bulk', action: 'approve', ids: selectedVisible });
                      setDecisionNote('');
                    }}
                  >
                    <CheckCircle2 size={14} /> {bulkBusy ? t('pa.working') : t('pa.decideSelected')}
                  </button>
                  <button type="button" className="pa-btn" onClick={() => setSelected([])}>{t('pa.clearSelection')}</button>
                </div>
              )}
            </div>

            {!rows.length && (
              <div className="pa-empty">
                {rawRows.length
                  ? t('pa.emptyFiltered')
                  : (tab === 'pending' ? t('pa.emptyPending') : t('pa.emptyTab', { tab: t(`pa.tab${tab.charAt(0).toUpperCase()}${tab.slice(1)}`) }))}
              </div>
            )}
            {!!rows.length && (
              /* ~15 rows tall, then it scrolls with the header pinned — the queue can be long. */
              <div className="pa-table-wrap pa-table-scroll">
                {/* The identity columns and Action stay put while the rest scrolls sideways —
                    these tables are 14+ columns wide. */}
                <table
                  className={`pa-table pa-sticky-cols ${showCheck ? 'has-check' : ''}`}
                  style={{ width: gridWidth, minWidth: gridWidth }}
                >
                  <colgroup>
                    {showCheck && <col style={{ width: CHECK_W }} />}
                    <col style={{ width: NO_W }} />
                    {columns.map((c) => <col key={c.key} style={{ width: c.width }} />)}
                    <col style={{ width: actionW }} />
                  </colgroup>
                  <thead>
                    <tr>
                      {showCheck && (
                        <th className="pa-check-col">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            disabled={!actionableIds.length}
                            title={actionableIds.length ? '' : t('pa.skuNoneYours')}
                            onChange={toggleAll}
                            aria-label={t('xPa.selectAll')}
                          />
                        </th>
                      )}
                      <th className="pa-no-col">{t('pa.colNo')}</th>
                      {columns.map((c, i) => (
                        <SortTh key={c.key} label={t(c.label)} className={i < 2 ? 'pa-freeze' : ''} {...thProps(c.key)} />
                      ))}
                      <th className="pa-actions-col">{t('pa.colAction')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((g, i) => (
                      <React.Fragment key={g.groupId}>
                      <tr
                        ref={g.groupId === focusId ? focusRow : undefined}
                        className={['pa-rowclick', selected.includes(g.groupId) ? 'on' : '',
                          openSkus[g.groupId] ? 'pa-open' : '',
                          g.groupId === focusId ? 'pa-focus' : ''].filter(Boolean).join(' ')}
                        /* THE WHOLE ROW OPENS IT — Max, 2026-09-19: "khi click vào thì sẽ expand ra
                           SKU của project đó luôn". The row also carries a name that opens the
                           project, five buttons and a tick box, so a click is only read as "open
                           the SKUs" when it landed on none of them: this screen has lost clicks to
                           something drawn on top of it five times, and a row-wide handler that
                           swallowed a button press would be the sixth. */
                        onClick={(e) => {
                          if (e.target.closest('button, a, input, label, select, textarea')) return;
                          toggleSkuPanel(g.groupId);
                        }}
                      >
                        {showCheck && (
                          <td className="pa-check-col">
                            {/* The project's own box is dead when nothing inside it is this
                                person's to act on — the same rule one level up. A live tick over a
                                project that would tick nothing is the thing being fixed here.
                                The row still OPENS on a click, so the items stay readable. */}
                            <input
                              type="checkbox"
                              checked={selected.includes(g.groupId)}
                              disabled={!skusOf(g.groupId).length}
                              title={skusOf(g.groupId).length ? '' : t('pa.skuNoneYours')}
                              onChange={() => toggleOne(g.groupId)}
                              aria-label={t('xPa.selectRow', { id: g.groupId })}
                            />
                          </td>
                        )}
                        <td className="pa-no-col">{i + 1}</td>
                        <td className="mono pa-freeze">
                          {/* The affordance is a chevron and not the whole row: the row already has
                              a name that opens the project and five buttons that do other things,
                              and a click that means two different things depending on where it
                              lands is the trap this screen has hit before. */}
                          <button
                            type="button"
                            className={`pa-skutoggle ${openSkus[g.groupId] ? 'on' : ''}`}
                            aria-expanded={!!openSkus[g.groupId]}
                            title={t('pa.skuToggle', { n: g.skuCount || 0 })}
                            onClick={() => toggleSkuPanel(g.groupId)}
                          >
                            <ChevronRight size={13} />
                          </button>
                          {g.groupId}
                        </td>
                        <td className="pa-name pa-freeze">
                          {/* The approver opens a project from its name to read it before deciding. */}
                          {/* Red name = the approver left a remark on this one. The column below
                              carries the text; this is what makes it findable in a list of 20. */}
                          <button
                            type="button"
                            className={`pa-link${g.decisionNote ? ' pa-has-note' : ''}`}
                            title={g.decisionNote || undefined}
                            onClick={() => openProject(g, 'view')}
                          >
                            {g.groupName || '—'}
                          </button>
                          {/* SAID IN WORDS — Jay: "no one can immediately recognize that Red means
                              Reject". The red name means the approver left a remark; this says what
                              happened and how many items it happened to. */}
                          {refusedOf(g).length > 0 && (
                            <span className="pa-rejchip" title={t('pa.rejectedChipHint', { n: refusedOf(g).length })}>
                              {t('pa.rejectedChip', { n: refusedOf(g).length })}
                            </span>
                          )}
                        </td>
                        <td>{g.teamName || g.teamKey || '—'}</td>
                        <td>{firstNameOf(g.managerNames)}</td>
                        <td>{firstNameOf(g.ownerName)}</td>
                        {/* Order here MUST match APPROVAL_COLUMNS above. The headers are generated
                            from that list while these cells are written out by hand, so adding a
                            column to one and not the other shifts every value one place to the
                            left — which is exactly what happened when Sales Rep. was added. */}
                        <td>{firstNameOf(g.salesRepName)}</td>
                        <td>{g.category || '—'}</td>
                        <td>{g.season || '—'}</td>
                        <td>{g.partnerName || '—'}</td>
                        {/* A launch that does not fit its season, or (still waiting for a decision) a month already gone, is
                            marked where the approver reads it — Jay 01/11 (projectLaunchCheck.js). */}
                        {(() => {
                          const lf = launchFlags(g);
                          const past = lf.past && tab === 'pending';
                          const flag = past || lf.mismatch;
                          const tip = [past && t('stx.launchPast', { month: monthToDisplay(g.targetLaunchMonth) }),
                            lf.mismatch && t('stx.launchMismatch', { month: monthToDisplay(g.targetLaunchMonth), season: g.season || '',
                              from: monthToDisplay(lf.window.from), to: monthToDisplay(lf.window.to) })].filter(Boolean).join(' ');
                          return (
                            <td className={flag ? `pa-launch-flag${past ? ' past' : ''}` : undefined} title={tip || undefined}
                              data-launch-flag={flag ? (past ? 'past' : 'season') : undefined}>
                              {monthToDisplay(g.targetLaunchMonth) || '—'}{flag ? ' ⚠' : ''}
                            </td>
                          );
                        })()}
                        <td className="num">{g.skuCount}</td>
                        <td>{g.createdBy || '—'}</td>
                        {(APPROVAL_EXTRA[tab] || []).map((c) => {
                          const isNote = ['rejectionReason', 'approvalNote', 'decisionNote'].includes(c.key);
                          const text = c.get(g);
                          if (!isNote || !text) {
                            return <td key={c.key} className={isNote ? 'pa-reason' : 'pa-when'}>{text || '—'}</td>;
                          }
                          /* One line until it is clicked. A button rather than a div with a handler:
                             this is the only way to read the rest of a long remark, so it has to be
                             reachable by keyboard and announce whether it is open. The full text is
                             still on `title`, so a hover answers a short one without a click. */
                          const noteKey = `${g.groupId}:${c.key}`;
                          const open = !!openNotes[noteKey];
                          return (
                            <td key={c.key} className="pa-reason">
                              <button
                                type="button"
                                className={`pa-note${open ? ' on' : ''}`}
                                aria-expanded={open}
                                title={open ? undefined : text}
                                onClick={() => setOpenNotes((prev) => ({ ...prev, [noteKey]: !prev[noteKey] }))}
                              >
                                {text}
                              </button>
                            </td>
                          );
                        })}
                        <td className="pa-actions-col">
                          <div className="pa-actions">
                            <button type="button" className="pa-btn" onClick={() => openProject(g, 'view')}>
                              <Eye size={14} /> {t('pa.btnView')}
                            </button>
                            {tab === 'pending' && (isAdmin || canResubmit) && (
                              <button
                                type="button"
                                className="pa-btn"
                                /* Submitting no longer freezes the project (Max, 2026-09-03, reversing
                                   the 2026-08-21 rule): a manager who spots a mistake after sending it
                                   can still fix it. The tooltip says the approver is already holding
                                   it, because the mail he received still shows the old values.
                                   Delete stays blocked once sent — see the Delete button below. */
                                title={!mayEditGroup(g) ? t(NOT_YOUR_TEAM_KEY)
                                  : (g.submittedAt ? t('pa.lockedSubmitted') : '')}
                                disabled={busyId === g.groupId || !mayEditGroup(g)}
                                onClick={() => openProject(g, 'edit')}
                              >
                                <Pencil size={14} /> {t('pa.btnEdit')}
                              </button>
                            )}
                            {/* Not yet asked about: the manager sends it. Already asked: say when, so
                                nobody wonders whether the CEO has seen it. Both halves carry the same
                                gate — the badge is the button's finished state, so it belongs to the
                                people who own the button, and the Action column reserves the slot on
                                exactly that condition. Ungated, it printed into a width nobody had
                                counted. */}
                            {tab === 'pending' && (isAdmin || canResubmit) && stillToSend(g).length > 0 && mayEditGroup(g) && (
                              (() => {
                                /* THE TICKS DECIDE HOW MUCH, AND THERE IS NO LONGER AN IMPLICIT
                                   "ALL" — Max, 2026-09-21: "chưa tick nữa mà nút submit đã
                                   available rùi". Nothing ticked used to mean send the whole
                                   project, which was the convenience this button was born with;
                                   once a tick became the way to say which items go, a live button
                                   over an empty selection was offering to send something nobody
                                   had chosen.
                                   Ticking the project row is still one click for all of them, and
                                   the dead button says so on its tooltip rather than leaving
                                   somebody to guess why it will not press. */
                                const pick = pickedOf(g).filter((r) => (r.approvalStatus || 'Pending') === 'Pending'
                                  && !r.requestedAt);
                                const partial = pick.length > 0 && pick.length < (g.skuRows || []).length;
                                return (
                                  <button
                                    type="button"
                                    className="pa-btn submit"
                                    disabled={submitBusy || busyId === g.groupId || !pick.length}
                                    title={pick.length
                                      ? (partial ? t('pa.submitSomeHint', { n: pick.length }) : '')
                                      : t('pa.submitPickFirst')}
                                    onClick={() => submitSkus(g, pick)}
                                  >
                                    <Mail size={14} /> {pick.length ? t('pa.submitSome', { n: pick.length }) : t('pa.submit')}
                                  </button>
                                );
                              })()
                            )}
                            {/* THE SLOT SUBMIT LEAVES BEHIND IS A REMIND BUTTON, not a dead label.
                                "Sent" said something true and offered nothing: the request is with
                                the approver and the only thing anybody wants from this row is to
                                chase it. The badge survives for the one case a reminder cannot
                                serve — everything sent has been decided, so there is nothing left to
                                remind him about — because a button that is always refused should
                                not be offered. Same shape as Ticket Station's Send/Remind.

                                IT DOES NOT WAIT FOR THE PROJECT TO BE SENT WHOLE. The badge it
                                replaced was drawn only once nothing was left to send, which is the
                                right rule for a label saying "this has gone" and the wrong one for
                                a button: partial submit exists so three SKUs can sit with the
                                approver while two are still being costed, and that project is
                                exactly the one somebody wants to chase. So Submit and Remind can
                                stand side by side, each about its own half — which is why the
                                Action column reserves two slots when any row needs both. */}
                            {tab === 'pending' && (isAdmin || canResubmit) && !!g.submittedAt
                              && stillToDecide(g).length > 0 && (
                              <button
                                type="button"
                                className="pa-btn submit"
                                disabled={submitBusy || busyId === g.groupId}
                                title={t('pa.remindHint', {
                                  when: fmtDateTimeVN(g.submittedAt, ''), n: stillToDecide(g).length,
                                })}
                                onClick={() => remindApproval(g)}
                              >
                                <Mail size={14} /> {t('pa.remind')}
                              </button>
                            )}
                            {tab === 'pending' && (isAdmin || canResubmit) && !!g.submittedAt && !stillToSend(g).length
                              && !stillToDecide(g).length && (
                              <span className="pa-sub-when" title={t('pa.submittedOn', { when: fmtDateTimeVN(g.submittedAt, '') })}>
                                <Mail size={13} /> {t('pa.sent')}
                              </span>
                            )}
                            {tab === 'pending' && (isAdmin || canResubmit)
                              && refusedOf(g).length > 0 && resubmitButton(g)}
                            {tab === 'pending' && (isAdmin || canResubmit) && dropButton(g)}
                            {tab === 'rejected' && (isAdmin || canResubmit) && (
                              <>
                                <button
                                  type="button"
                                  className="pa-btn"
                                  title={mayEditGroup(g) ? '' : t(NOT_YOUR_TEAM_KEY)}
                                  disabled={busyId === g.groupId || !mayEditGroup(g)}
                                  onClick={() => openProject(g, 'edit')}
                                >
                                  <Pencil size={14} /> {t('pa.btnEdit')}
                                </button>
                                {resubmitButton(g)}
                                {/* Last, behind Edit and Resubmit — the two somebody reaches for on a
                                    rejected project — rather than beside View. */}
                                {dropButton(g)}
                              </>
                            )}
                            {tab === 'pending' && canApprove && stillToDecide(g).length > 0 && (
                              <>
                                {(() => {
                                  /* The same rule as Submit beside it: the ticks say how much of
                                     the project this is about. Nothing ticked means every item the
                                     approver has been sent; tick two and the dialog asks about
                                     those two and leaves the rest where they are. */
                                  const pick = pickedOf(g).filter((r) => (r.approvalStatus || 'Pending') === 'Pending'
                                    && r.requestedAt);
                                  const partial = pick.length > 0 && pick.length < (g.skuRows || []).length;
                                  return (
                                    <button
                                      type="button"
                                      className="pa-btn approve"
                                      disabled={busyId === g.groupId}
                                      title={partial ? t('pa.approveSomeHint', { n: pick.length }) : ''}
                                      onClick={() => {
                                        setDecisionFor({
                                          mode: 'single',
                                          action: 'approve',
                                          group: pick.length ? { ...g, skuRows: pick } : g,
                                        });
                                        setDecisionNote('');
                                      }}
                                    >
                                      <CheckCircle2 size={14} /> {partial ? t('pa.approveSome', { n: pick.length }) : t('pa.btnApprove')}
                                    </button>
                                  );
                                })()}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* THE SKU LIST, under the row it belongs to — Max, 2026-09-19. A project is
                          a bundle, and the whole reason the decision moved down to the SKU is that
                          what is in the bundle matters; making somebody open a dialog to find out
                          is making them decide before they have looked. */}
                      {openSkus[g.groupId] && (
                        <tr className="pa-skurow">
                          <td colSpan={99}>
                            <div className="pa-skupanel">
                              {!(g.skuRows || []).length ? (
                                <p className="pa-skunone">{t('pa.skuNone')}</p>
                              ) : (
                                <>
                                  <table className="pa-skutable">
                                    <thead>
                                      <tr>
                                        <th className="pick" />
                                        <th>{t('pa.skuId')}</th>
                                        <th>{t('pa.skuItem')}</th>
                                        <th>{t('pa.skuCategory')}</th>
                                        <th className="num">{t('pa.skuQty')}</th>
                                        <th className="num">{t('pa.skuPrice')}</th>
                                        <th>{t('pa.skuExf')}</th>
                                        <th>{t('pa.skuPic')}</th>
                                        <th>{t('pa.skuStatus')}</th>
                                        <th>{t('pa.skuState')}</th>
                                        <th>{t('pa.skuNote')}</th>
                                        {/* The two halves of the conversation about one item, side
                                            by side: what the approver refused it for, and what the
                                            manager changed when sending it back. */}
                                        <th>{t('pa.skuMgrNote')}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(g.skuRows || []).map((r) => {
                                        const st = r.approvalStatus || 'Pending';
                                        /* THREE STATES, not two: waiting for an answer and never
                                           having been asked look identical on a row and are not the
                                           same thing at all — one is the approver's move, the other
                                           is the requester's. */
                                        const sent = !!r.requestedAt;
                                        const key = st === 'Pending' ? (sent ? 'waiting' : 'unsent') : st.toLowerCase();
                                        return (
                                          <tr key={r.projectId} className={`s-${key}${skuActionable(r) ? '' : ' dead'}`}>
                                            <td className="pick">
                                              <input
                                                type="checkbox"
                                                checked={!!pickedSkus[r.projectId]}
                                                disabled={!skuActionable(r)}
                                                title={skuActionable(r) ? '' : skuWhyDead(r)}
                                                onChange={() => toggleSku(r.projectId)}
                                                aria-label={t('xPa.selectRow', { id: r.projectId })}
                                              />
                                            </td>
                                            <td className="mono">{r.projectId}</td>
                                            <td className="pa-skuitem" title={r.remark || undefined}>
                                              {r.itemName || '—'}
                                              {/* The style code is what the vendor and the sheets
                                                  call this item, so it rides under the name rather
                                                  than claiming a column of its own. */}
                                              {!!r.styleCode && <em>{r.styleCode}</em>}
                                            </td>
                                            <td>{r.category || '—'}{r.typology ? ` · ${r.typology}` : ''}</td>
                                            <td className="num">{r.qty ? Number(r.qty).toLocaleString('en-GB') : '—'}</td>
                                            <td className="num">{r.targetPrice ? `$${Number(r.targetPrice).toFixed(2)}` : '—'}</td>
                                            <td className="nw">{r.exf || t('pa.skuTba')}</td>
                                            {/* firstNameOf answers '—' for a blank, so joining two
                                                blanks printed "—, —". The pair is joined first and
                                                shortened once. */}
                                            <td>{firstNameOf([r.developer, r.designer].filter(Boolean).join(', '))}</td>
                                            <td>{r.status || '—'}</td>
                                            <td><span className={`pa-skupill ${key}`}>{t(`pa.skuSt_${key}`)}</span></td>
                                            <td className="pa-skunote" title={r.decisionNote || undefined}>{r.decisionNote || ''}</td>
                                            <td className="pa-skunote" title={r.resubmitNote || undefined}>{r.resubmitNote || ''}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                  {/* NO BUTTONS HERE — Max, 2026-09-20: "có 2 chỗ submit thì sẽ
                                      bị confuse á". Submit and Approve lived on the row AND in this
                                      panel, and the pair of them said different things about the
                                      same project. The panel is for LOOKING and CHOOSING; the
                                      Action column is where a project is acted on, and the ticks
                                      below decide how much of it. */}
                                  <div className="pa-skuacts">
                                    <span className="pa-skucount">
                                      {t('pa.skuPicked', { n: pickedOf(g).length })}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}


        {decisionFor && (
          <DecisionDialog
            spec={decisionFor}
            note={decisionNote}
            onNote={setDecisionNote}
            busy={decisionFor.mode === 'bulk' ? bulkBusy : busyId === decisionFor.group?.groupId}
            selectedIds={selectedVisible}
            onSwitch={(action) => setDecisionFor((d) => ({ ...d, action }))}
            onCancel={() => { setDecisionFor(null); setDecisionNote(''); }}
            onConfirm={(decisions) => {
              const note = decisionNote.trim();
              if (decisionFor.mode === 'bulk') return bulkDecide(decisionFor.action, note);
              // The resubmit dialog hands back one note per item; the group note stays beside them.
              if (decisionFor.action === 'resubmit') {
                return resubmit(decisionFor.group, note, decisions?.skuNotes || {});
              }
              /* The endpoint is chosen by what the rows say, not by the button that opened the
                 dialog: an approver can open Approve and cross every item, and the URL has to
                 follow him rather than the other way round. */
              const anyYes = Array.isArray(decisions) && decisions.some((d) => d.decision === 'Approved');
              const action = Array.isArray(decisions) ? (anyYes ? 'approve' : 'reject') : decisionFor.action;
              return decide(decisionFor.group, action, note, decisions);
            }}
          />
        )}

        {/* Deleting a project: what goes with it, in the words the server used. The reason box is
            required only when tickets are attached — those people get a cancellation mail. */}
        {!!dropGroup && (
          <div className="st-modal-overlay pa-over" role="dialog" aria-modal="true">
            <div className="st-modal pa-dropbox">
              <div className="st-modal-head">
                <h3>{t('pa.dropHead', { id: dropGroup.group.groupId })}</h3>
                <button type="button" className="st-modal-close" aria-label={t('pa.btnClose')} onClick={() => setDropGroup(null)}>
                  &times;
                </button>
              </div>
              <div className="st-modal-body">
                {!!dropErr && <div className="pa-form-missing">{dropErr}</div>}
                <p className="pa-dropname">{dropGroup.group.groupName}</p>
                {dropGroup.loading ? (
                  <p className="pt-help">{t('pa.dropChecking')}</p>
                ) : (
                  <>
                    <p className="pt-help">
                      {dropGroup.skus === 1 ? t('pa.dropWarnOne') : t('pa.dropWarnMany', { n: dropGroup.skus })}
                    </p>
                    {!!dropGroup.tickets.length && (
                      <>
                        <p className="pa-droptickets">
                          {dropGroup.tickets.length === 1
                            ? t('pa.dropTicketsOne')
                            : t('pa.dropTicketsMany', { n: dropGroup.tickets.length })}
                        </p>
                        <ul className="pa-dropticketlist">
                          {/* The preflight names these taskId / name / receivingTeam / notified.
                              Reading the database's own column names here printed an empty bullet
                              per ticket — the count was right, the list said nothing — and gave
                              every <li> the same undefined key. */}
                          {dropGroup.tickets.map((tk) => (
                            <li key={tk.taskId}>
                              <strong>{tk.taskId}</strong> {tk.name}
                              {tk.receivingTeam ? ` — ${tk.receivingTeam}` : ''}
                              {tk.notified ? ` (${t('pa.dropEmailed')})` : ''}
                            </li>
                          ))}
                        </ul>
                        <label className="pa-dropreason">
                          <span>{t('pa.dropReason')}</span>
                          <input value={dropReason} onChange={(e) => setDropReason(e.target.value)} />
                        </label>
                      </>
                    )}
                  </>
                )}
              </div>
              <div className="st-modal-foot st-form-actions">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDropGroup(null)}>
                  {t('pa.btnCancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  disabled={dropGroup.loading || dropBusy || (!!dropGroup.tickets.length && !dropReason.trim())}
                  onClick={doDeleteGroup}
                >
                  {dropBusy ? t('pa.dropBusy') : t('pa.dropConfirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {editFor && (
          <EditProjectModal
            group={editFor.group}
            config={config}
            viewer={me}
            mode={editFor.mode}
            saving={busyId === editFor.group.groupId}
            onClose={() => setEditFor(null)}
            filterOptions={filterOptions}
            skuRows={skuRows}
            onSave={(patch) => saveEdit(editFor.group, patch)}
            saveError={editError}
            onAddPartner={canResubmit ? addPartner : null}
            onAddSalesRep={addSalesRep}
            /* The deleted SKU is already gone on the server; refresh so the list behind the modal
               stops showing it. */
            onSkuDeleted={() => { setMessage(t('xPa.skuDeleted')); load(); }}
          />
        )}

      </main>
    </div>
  );
};

/* The decision dialog moved to its own module on 2026-09-11 — see DecisionDialog.jsx for why.
   Re-exported here because this screen is where it was, and a reader looking for it should be told
   where it went rather than find nothing. */

/* ---- Open a project: view it, or fix it after a rejection ---------------------------
   Deliberately the SAME form as the tracker's New Project screen (Max 2026-08-11) — same field
   order, same searchable Partner picker, same derived Project Name — so a manager correcting a
   rejection is not learning a second layout. The pieces are imported from the tracker rather than
   re-implemented, so the two can never drift apart.
   In `view` mode every control is disabled: the approver opens this to read the project before
   deciding, not to change it. */
// The tracker stores a project row; SkuCard reads a form draft. One mapper keeps the read-only view
// honest — every field it shows comes from the same row the tracker shows.
const projectToSkuView = (p) => ({
  projectId: p.id,
  item: p.subItem || p.projectItem || '',
  itemCategory: p.productType || '',
  typology: p.typology || '',
  frameColor: p.frameColor || '',
  assigned: p.assignedTo || '',
  designer: p.designerName || '',
  allocation: p.allocation || '',
  qty: p.estimateQty ?? '',
  targetPrice: p.targetPrice ?? '',
  startDate: p.startDate || '',
  exfDate: p.exfShipDate || '',
  launchDate: p.launchDate || '',
  // The row as it was opened: an existing SKU is held to the required fields it already carries.
  _was: skuWas(p),
  status: p.status || '',
  remark: p.remark || '',
  samples: p.sampleId || '',
  repeatOrder: !!p.repeatOrder,
  // Keys come from the tracker's own list rather than a copy — one of them is `handOffMeeting`,
  // and a hand-typed duplicate had already got that capital O wrong.
  stages: stageColumns.reduce((acc, [k]) => ({ ...acc, [k]: p[k] || 'Pending' }), {}),
});

const EditProjectModal = ({ group, config, filterOptions, skuRows = [], mode, saving, onClose, onSave, onAddPartner, onAddSalesRep, onSkuDeleted, saveError = '', viewer = null }) => {
  const { t } = useTranslation();
  const readOnly = mode === 'view';
  /* Deleting an existing SKU used to be Project Tracker's job only, on the grounds that it also
     detaches the sample rounds hanging off it. Max asked for it here too (2026-09-02); the warning
     now carries that consequence instead of the button being absent. */
  const [dropSku, setDropSku] = useState(null);
  const [dropErr, setDropErr] = useState('');
  const [dropping, setDropping] = useState(false);

  const removeSku = async () => {
    setDropping(true); setDropErr('');
    try {
      const data = await api(`/projects/${encodeURIComponent(dropSku.projectId)}`, { method: 'DELETE' });
      setSkuEdits((list) => list.filter((x) => x.projectId !== dropSku.projectId));
      setDropSku(null);
      if (onSkuDeleted) onSkuDeleted(dropSku.projectId, data);
    } catch (err) { setDropErr(err.message); } finally { setDropping(false); }
  };
  const [form, setForm] = useState({
    teamKey: group.teamKey || '',
    ownerUserId: group.ownerUserId ? String(group.ownerUserId) : '',
    salesRepId: group.salesRepId ? String(group.salesRepId) : '',
    category: group.category || '',
    // Season is stored three ways: the Year, the ticked parts, and the label ('26FW') the two build.
    // Only the label was seeded here, so the Year select and the Season boxes opened blank on every
    // project that had them — the screen said "not set" about data that was in the row (Max,
    // 2026-09-03, PRJ-142). The API has returned both since the field was introduced.
    season: group.season || '',
    seasonYear: group.seasonYear ? String(group.seasonYear) : '',
    seasonParts: seasonPartsOrdered(group.seasonParts),
    // Not editable here — read by the name preview so it can tell "keeps its name" from "gets rebuilt".
    groupName: group.groupName || '',
    partnerId: group.partnerId ? String(group.partnerId) : '',
    keyword: group.keyword || '',
    targetLaunchMonth: group.targetLaunchMonth || '',
    projectCase: group.projectCase || '',
    entity: group.entity || '',
    // Editable here too since 2026-08-19 — the manager fixing a project for approval is exactly the
    // person who needs to correct what the approver will read.
    approvalNote: group.approvalNote || '',
    projectPurpose: group.projectPurpose || '',
  });
  // SKUs added here are created on save; the ones already in the group are listed read-only.
  const [newSkus, setNewSkus] = useState([]);
  const addSku = () => setNewSkus((list) => [...list, emptySku()]);
  const existingSkus = useMemo(
    () => skuRows.filter((p) => p.groupId === group.groupId).map(projectToSkuView),
    [skuRows, group.groupId],
  );
  // Max, 2026-08-19: the SKUs already in the project are editable here too. They used to render
  // locked, which meant a manager fixing a project for approval had to finish the job in Project
  // Tracker. Held as state so only the cards actually touched are sent on save.
  const [skuEdits, setSkuEdits] = useState(existingSkus);
  const [touchedSkus, setTouchedSkus] = useState({});
  useEffect(() => { setSkuEdits(existingSkus); setTouchedSkus({}); }, [existingSkus]);

  // Esc / × / Cancel all ask first, and only when something was actually changed — View mode and
  // "opened it to read it" close straight away. `touchedSkus` is the SKU cards' own dirty flag, so
  // it is passed in rather than diffed: the rows are re-seeded from the server on every reload.
  const { requestClose, prompt: discardPrompt } = useDiscardGuard(
    { form, newSkus },
    onClose,
    { active: !saving, extraDirty: Object.keys(touchedSkus).length > 0 },
  );

  const teams = config?.teams || [];
  const partners = config?.partners || [];
  const categories = config?.categories || [];
  const seasons = useMemo(() => seasonOptions(new Date().getFullYear()), []);
  const partnerName = (partners.find((p) => String(p.partnerId) === String(form.partnerId)) || {}).name || '';

  const missing = [
    !form.teamKey && t('pa.fTeam'),
    !form.category && t('pa.fCategory'),
    !form.season && t('pa.fSeason'),
    !form.targetLaunchMonth && t('pa.fTargetLaunch'),
    !form.ownerUserId && t('pa.fOwner'),
    !form.salesRepId && t('pa.colSalesRep'),
    // Exempt for the projects that existed before the rule — otherwise a manager could not touch
    // one of them without inventing a purpose after the fact.
    group.purposeRequired !== false && !String(form.projectPurpose || '').trim() && t('st.pfPurpose'),
    /* The SKU's four required fields (Max, 2026-10-16) — on every SKU being added, and on an existing
       one that was touched, for the fields it already carried. */
    ...newSkus
      .filter((sk) => String(sk.item || '').trim() || String(sk.itemCategory || '').trim() || String(sk.assigned || '').trim())
      .map((sk, i) => { const m = skuCardMissing(sk); return m.length ? `${sk.item || t('xPa.newSkuN', { n: i + 1 })}: ${m.join(', ')}` : null; }),
    ...skuEdits
      .filter((sk) => sk.projectId && touchedSkus[sk.projectId])
      .map((sk) => { const m = skuCardMissing(sk); return m.length ? `${sk.projectId}: ${m.join(', ')}` : null; }),
  ].filter(Boolean);

  /* Target Launching against today and the season (projectLaunchCheck.js, Jay 01/11) — only what this save changes. */
  const launchErr = readOnly ? [] : launchErrors(launchCheck(form, group), form.launchConfirmed, t);

  const submit = () => {
    if (missing.length || launchErr.length) return;
    onSave({
      skus: newSkus.filter((sk) => String(sk.item || '').trim() || String(sk.itemCategory || '').trim() || String(sk.assigned || '').trim()),
      editedSkus: skuEdits.filter((sk) => sk.projectId && touchedSkus[sk.projectId]),
      teamKey: form.teamKey,
      ownerUserId: form.ownerUserId || null,
      salesRepId: form.salesRepId || null,
      category: form.category,
      season: form.season,
      /* The Year and the ticked parts are what the server rebuilds the label from; sending only the
         label left season_year / season_parts frozen at whatever they were, so re-ticking a season
         appeared to do nothing. Sent only when the form actually holds them: for the 11 legacy rows
         that carry a label and no parts, an empty Year would make the server recompute the label as
         NULL and wipe the season off the project. */
      ...(form.seasonYear || seasonPartsOrdered(form.seasonParts).length
        ? { seasonYear: form.seasonYear || null, seasonParts: seasonPartsOrdered(form.seasonParts) }
        : {}),
      partnerId: form.partnerId || null,
      keyword: form.keyword,
      targetLaunchMonth: form.targetLaunchMonth,
      projectCase: form.projectCase,
      entity: form.projectCase === 'B' ? form.entity : '',
      approvalNote: form.approvalNote,
      projectPurpose: form.projectPurpose,
    });
  };

  return (
    // `.st-page` carries the tracker's CSS custom properties (--hevn-bg, --line, …). Without it the
    // section bands and borders render unstyled, because those variables are scoped to that class.
    <div className="st-page pa-modal-host">
      <div className="st-modal-overlay" role="dialog" aria-modal="true">
      {/* Portals to <body>; only rendered once the form is dirty and the user tries to leave. */}
      {discardPrompt}
      <div className="st-modal">
        <div className="st-modal-head">
          <h3>{readOnly ? `${group.groupId} — ${group.groupName}` : t('xPa.editTitle', { id: group.groupId })}</h3>
          <button type="button" className="st-modal-close" onClick={requestClose} aria-label={t('pa.btnClose')}>&times;</button>
        </div>
        <div className="st-modal-body">
          {group.approvalStatus === 'Rejected' && group.rejectionReason && (
            <div className="pa-reject-note">
              <strong>{t('pa.rejectedBy', { who: group.approvedBy || '—' })}</strong> {group.rejectionReason}
            </div>
          )}
          {/* The requester's note is no longer printed here — it has a real field inside Project
              Information now, so printing it twice was just noise. What DOES belong here is the
              other direction: what the approver said when he decided. */}
          {!!group.decisionNote && (
            <div className="pa-manager-note">
              <strong>{t('pa.approverNoteLabel')}</strong> {group.decisionNote}
            </div>
          )}

          <div className="own-section own-hevn">
            <div className="own-hd own-hd-sub">
              <span>{t('pa.secProject')}</span>
              <small>{t('xPa.secProjectSub')}</small>
            </div>
            <div className="own-body">
              <ProjectInfoFields
                value={form}
                onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
                readOnly={readOnly}
                purposeRequired={group.purposeRequired !== false}
                teams={teams}
                partners={partners}
                salesReps={config?.salesReps || []}
                categories={categories}
                seasons={seasons}
                canAddPartner={!!onAddPartner}
                onAddPartner={onAddPartner}
                canAddSalesRep={!!onAddSalesRep}
                onAddSalesRep={onAddSalesRep}
                names={{ partnerName, salesRepName: group.salesRepName }}
                nameFallback={t('xPa.fillSeason')}
                /* So the block can warn before the owner is handed over — the one change on this
                   form that takes the project away from the person making it. */
                viewer={viewer}
                launchBefore={group}
              />
            </div>
          </div>

          <div className="own-section own-both">
            <div className="own-hd" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{t('pa.secSku')}</span>
              {!readOnly && <button type="button" className="btn btn-ghost btn-sm" onClick={addSku}>{t('pa.addSku')}</button>}
            </div>
            <div className="own-body">
              {/* Existing SKUs expand exactly like the New Project form, just locked — the approver
                  needs to see what is actually inside the project before deciding. */}
              {!!existingSkus.length && (
                <div className="pt-sku-list" style={{ marginBottom: newSkus.length ? 10 : 0 }}>
                  {skuEdits.map((sku, idx) => (
                    <SkuCard
                      key={sku.projectId || idx}
                      index={idx}
                      sku={sku}
                      filterOptions={filterOptions}
                      defaultOpen={false}
                      readOnly={readOnly}
                      onChange={(patch) => {
                        setSkuEdits((list) => list.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
                        setTouchedSkus((m) => ({ ...m, [sku.projectId]: true }));
                      }}
                      /* Deleting is immediate and hits the server — unlike a new SKU's × which just
                         drops an unsaved draft — so it goes through a confirmation that says what
                         else it takes with it. */
                      onRemove={readOnly ? null : () => { setDropErr(''); setDropSku(sku); }}
                    />
                  ))}
                </div>
              )}
              {!existingSkus.length && !newSkus.length && (
                <div className="pt-help">{t('pa.noSkuYet')}</div>
              )}
              {/* A new SKU belongs directly under the ones already there. The photos block used to sit
                  between the two lists, so pressing Add SKU dropped the new card below Sample photos,
                  away from the list it joins (Max, 2026-09-02). */}
              {!readOnly && (
                <>
                  {!!newSkus.length && (
                    <div className="pt-sku-list">
                      {newSkus.map((sku, idx) => (
                        <SkuCard
                          key={idx}
                          index={(group.skus || []).length + idx}
                          sku={sku}
                          filterOptions={filterOptions}
                          defaultOpen={idx === newSkus.length - 1}
                          onChange={(patch) => setNewSkus((list) => list.map((x, i) => (i === idx ? { ...x, ...patch } : x)))}
                          onRemove={() => setNewSkus((list) => list.filter((_, i) => i !== idx))}
                        />
                      ))}
                    </div>
                  )}
                  <div className="pt-help" style={{ marginTop: 8 }}>{t('pa.skuSaveHint')}</div>
                </>
              )}
              {/* Young asked to see the sample before approving, so the photos sit in the same modal
                  as the details — in view mode too, which is the mode the approver opens. */}
              <div style={{ marginTop: 14, borderTop: '1px solid #eef2f7', paddingTop: 12 }}>
                {/* The approver sees the items he was ASKED about: the roll-up filters itself to
                    the submitted SKUs, so a project part-sent does not show him pictures of work
                    still being costed. */}
                <ProjectPhotos rollupGroupId={group.groupId} />
              </div>
            </div>
          </div>
        </div>
        {/* Immediate and irreversible, and it takes the SKU's sample rounds off it — so the dialog
            says that rather than asking "are you sure". */}
        {!!dropSku && (
          <div className="st-modal-overlay pa-over" role="dialog" aria-modal="true">
            <div className="st-modal pa-dropbox">
              <div className="st-modal-head">
                <h3>{t('xPa.dropSkuHead')}</h3>
                <button type="button" className="st-modal-close" aria-label={t('pa.btnClose')} onClick={() => setDropSku(null)}>
                  &times;
                </button>
              </div>
              <div className="st-modal-body">
                {!!dropErr && <div className="pa-form-missing">{dropErr}</div>}
                <p className="pa-dropname">{dropSku.projectId} — {dropSku.item || dropSku.projectItem}</p>
                <p className="pt-help">{t('xPa.dropSkuBody')}</p>
              </div>
              <div className="st-modal-foot st-form-actions">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDropSku(null)}>
                  {t('pa.btnCancel')}
                </button>
                <button type="button" className="btn btn-danger btn-sm" disabled={dropping} onClick={removeSku}>
                  {dropping ? '…' : t('xPa.dropSkuBtn')}
                </button>
              </div>
            </div>
          </div>
        )}

        {!readOnly && missing.length > 0 && (
          <div className="pa-form-missing">
            {t('pa.fillRequired')} <strong>{missing.join(', ')}</strong>
          </div>
        )}
        {!readOnly && !missing.length && launchErr.length > 0 && (
          <div className="pa-form-missing" data-launch-block>{launchErr.join(' ')}</div>
        )}
        {!readOnly && !missing.length && !!saveError && (
          <div className="pa-form-missing">{t('pa.couldNotSave')} <strong>{saveError}</strong></div>
        )}
        <div className="st-modal-foot st-form-actions">
          <button type="button" className="btn btn-ghost btn-sm" onClick={requestClose}>
            {readOnly ? t('pa.btnClose') : t('pa.btnCancel')}
          </button>
          {!readOnly && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={saving || missing.length > 0 || launchErr.length > 0}
              onClick={submit}
            >
              {saving ? t('pa.saving') : t('pa.btnSaveChanges')}
            </button>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

/* ---- Settings: staff the two teams + point the approval mail somewhere -------------- */

const SettingsPanel = ({ config, onSaved, onError }) => {
  const { t } = useTranslation();
  const [approval, setApproval] = useState(() => config?.approval || { enabled: true, recipient: '', cc: '' });
  const [savingMail, setSavingMail] = useState(false);

  useEffect(() => { setApproval(config?.approval || { enabled: true, recipient: '', cc: '' }); }, [config]);


  const saveMail = async () => {
    setSavingMail(true);
    try {
      await api('/approval-settings', {
        method: 'PUT',
        body: JSON.stringify(approval),
      });
      onSaved(t('pa.setSaved'));
    } catch (e) {
      onError(e.message);
    } finally {
      setSavingMail(false);
    }
  };

  return (
    <>
      <div className="pa-section">
        <h3 className="pa-sec-title"><Mail size={15} /> {t('pa.setTitle')}</h3>
        <p className="pa-sec-note">{t('pa.setNote')}</p>
        <div className="pa-form">
          <label>
            <span>{t('pa.setApprover')}</span>
            <input
              type="email"
              value={approval.recipient}
              onChange={(e) => setApproval((a) => ({ ...a, recipient: e.target.value }))}
              placeholder="y@helinox.com"
            />
          </label>
          <label>
            <span>{t('pa.setCc')}</span>
            <input
              type="text"
              value={approval.cc}
              onChange={(e) => setApproval((a) => ({ ...a, cc: e.target.value }))}
              placeholder="jay@helinox.com, max@helinox.com"
            />
          </label>
          <label className="pa-check">
            <input
              type="checkbox"
              checked={!!approval.enabled}
              onChange={(e) => setApproval((a) => ({ ...a, enabled: e.target.checked }))}
            />
            <span>{t('pa.setGate')}</span>
          </label>
        </div>
        <button type="button" className="pa-btn primary" disabled={savingMail} onClick={saveMail}>
          {savingMail ? t('pa.saving') : t('pa.setSave')}
        </button>
      </div>


    </>
  );
};

export default ProjectApproval;
